import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {AppConfigurationService} from '@shared/app-configuration.service';
import {Observable} from 'rxjs';
import {
  PlanningEventType,
  ResourceReservationEvent,
  SchoolDiaryEntryEvent,
  StudentAbsenceEvent,
  TeacherPlanningEvent,
  TeacherPlanningPersonalNoteEvent
} from '@model/teacher-planning-event';
import {map} from 'rxjs/operators';
import {DateTime} from 'luxon';
import {getDateString, getDateTime} from '@shared/util/date-util';

@Injectable({
  providedIn: 'root'
})
export class TeacherPlanningService {

  private readonly _apiUrl: string;

  constructor(private _http: HttpClient, private _appConfig: AppConfigurationService) {
    this._apiUrl = `${this._appConfig.getConfig().apiUrl}/schools`;
  }

  saveTeacherPlanningEvent(schoolId: string, teacherPlanningEvent: TeacherPlanningEvent): Observable<TeacherPlanningEvent> {
    const body =  {
      ...teacherPlanningEvent,
      date: getDateString(teacherPlanningEvent.date)
    };
    if (teacherPlanningEvent.id) {
      return this._http.put(`${this._apiUrl}/${schoolId}/teacherPlanning/events/${teacherPlanningEvent.id}`, body)
        .pipe(
          map(event => this._mapToSpecificEvent(event as TeacherPlanningEvent))
        );
    } else {
      return this._http.post(`${this._apiUrl}/${schoolId}/teacherPlanning/events`, body)
        .pipe(
          map(event => this._mapToSpecificEvent(event as TeacherPlanningEvent))
        );
    }
  }

  loadTeacherPlanningEvents(schoolId: string, startDate: DateTime, endDate: DateTime): Observable<TeacherPlanningEvent[]> {
    const parameters = new HttpParams()
      .set('from', getDateString(startDate))
      .set('to', getDateString(endDate));
    return this._http.get(`${this._apiUrl}/${schoolId}/teacherPlanning/events`, {params: parameters})
      .pipe(
        map(events => this._mapToSpecificEvents(events as TeacherPlanningEvent[]))
      );
  }

  deleteTeacherPlanningEvent(schoolId: string, eventId: string): Observable<void> {
    return this._http.delete<void>(`${this._apiUrl}/${schoolId}/teacherPlanning/events/${eventId}`);
  }

  private _mapToSpecificEvents(events: any[]): TeacherPlanningEvent[] {
    return events && events.map(e => this._mapToSpecificEvent(e)).filter(e => !!e);
  }

  private _mapToSpecificEvent(event: any): TeacherPlanningEvent {
    switch (event.type) {
      case PlanningEventType.PERSONAL_NOTE:
        return {
          id: event.id,
          date: DateTime.fromISO(event.date),
          hour: event.hour,
          type: PlanningEventType.PERSONAL_NOTE,
          note: event.note
        } as TeacherPlanningPersonalNoteEvent;
      case PlanningEventType.STUDENT_ABSENCE:
        return {
          id: event.id,
          date: DateTime.fromISO(event.date),
          hour: event.hour,
          type: PlanningEventType.STUDENT_ABSENCE,
          firstName: event.firstName,
          lastName: event.lastName
        } as StudentAbsenceEvent;
      case PlanningEventType.SCHOOL_DIARY:
        return {
          id: event.id,
          date: DateTime.fromISO(event.date),
          hour: event.hour,
          type: PlanningEventType.SCHOOL_DIARY,
          attributionId: event.attributionId,
          lessonSubject: event.lessonSubject,
          homework: event.homework,
          homeworkUpdateDate: event.homeworkUpdateDate ? DateTime.fromISO(event.homeworkUpdateDate) : undefined,
          homeworkCreationDate: event.homeworkCreationDate ? DateTime.fromISO(event.homeworkCreationDate) : undefined,
          remark: event.remark,
          lessonName: event.lessonName,
          attributionPseudo: event.attributionPseudo
        } as SchoolDiaryEntryEvent;
      case PlanningEventType.RESOURCE_RESERVATION:
        return {
          id: event.id,
          date: DateTime.fromISO(event.date),
          hour: event.hour,
          type: PlanningEventType.RESOURCE_RESERVATION,
          reference: event.reference,
        } as ResourceReservationEvent;
      default:
        return undefined;
    }
  }

  loadSchoolDiaryByAttribution(schoolId: string, attributionId: number): Observable<SchoolDiaryEntryEvent[]> {
    const parameters = new HttpParams()
      .set('attributionId', `${attributionId}`);
    return this._http.get<SchoolDiaryEntryEvent[]>(`${this._apiUrl}/${schoolId}/teacherPlanning/diaryEvents`, {params: parameters})
      .pipe(
        map(events => this._mapToSpecificEvents(events as TeacherPlanningEvent[]))
      );
  }

  loadSchoolDiaryByClass(schoolId: string, classId: number, from: DateTime, to: DateTime): Observable<SchoolDiaryEntryEvent[]> {
    const params = new HttpParams()
      .set('classId', `${classId}`)
      .set('from', getDateString(from))
      .set('to', getDateString(to));
    return this._http.get<SchoolDiaryEntryEvent[]>(`${this._apiUrl}/${schoolId}/teacherPlanning/diaryEvents`, {params})
      .pipe(
        map(events => events.map(event => ({
          ...event,
          date: getDateTime(event.date),
          homeworkUpdateDate: event.homeworkUpdateDate ? DateTime.fromISO(event.homeworkUpdateDate as any) : undefined,
          homeworkCreationDate: event.homeworkCreationDate ? DateTime.fromISO(event.homeworkCreationDate as any) : undefined,
        }) as SchoolDiaryEntryEvent))
      );
  }

  createSchoolDiaryByAttributionPdfGenerationRequest(schoolId: string, attributionId: number): Observable<string> {
    const parameters = new HttpParams()
      .set('attributionId', attributionId);
    return this._http.post<string>(
      `${this._apiUrl}/${schoolId}/teacherPlanning/diaryEvents/generateReport`, {},
      {params: parameters, observe: 'response'})
      .pipe(
        map(response => response.headers.get('Location'))
      );
  }
}
